/**
 * CartContext — persists to localStorage for all users.
 * Logged-in users get a user-scoped key; guests use a shared key.
 * Cookie fallback ensures guest carts survive browser storage clears.
 */
import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react'

// ── Types ─────────────────────────────────────────────────────────────────────

export type CartItemType =
  | 'course'
  | 'program'
  | 'class_pack'
  | 'study_group'
  | 'session'
  | 'workshop'
  | 'membership'

export interface CartProvider {
  id: string
  name: string
  type: 'practitioner' | 'studio' | 'platform'
  avatarUrl?: string
  profileHref?: string
}

export interface CartItem {
  cartId: string           // unique per cart slot
  productId: string
  type: CartItemType
  title: string
  subtitle?: string
  price: number            // USD cents for precision, displayed as dollars
  currency: string
  quantity: number
  imageUrl?: string
  provider: CartProvider
  meta?: {
    duration?: string
    sessions?: number
    level?: string
    tags?: string[]
    startDate?: string
  }
}

export interface CartState {
  items: CartItem[]
  isOpen: boolean
  couponCode: string | null
  discountPct: number
}

type CartAction =
  | { type: 'ADD_ITEM'; item: Omit<CartItem, 'cartId'> }
  | { type: 'REMOVE_ITEM'; cartId: string }
  | { type: 'UPDATE_QTY'; cartId: string; qty: number }
  | { type: 'CLEAR' }
  | { type: 'OPEN' }
  | { type: 'CLOSE' }
  | { type: 'TOGGLE' }
  | { type: 'HYDRATE'; items: CartItem[] }
  | { type: 'APPLY_COUPON'; code: string; discountPct: number }
  | { type: 'REMOVE_COUPON' }

// ── Selectors ─────────────────────────────────────────────────────────────────

export function cartSubtotal(items: CartItem[]): number {
  return items.reduce((s, i) => s + i.price * i.quantity, 0)
}

export function cartTotal(items: CartItem[], discountPct: number): number {
  const sub = cartSubtotal(items)
  return sub * (1 - discountPct / 100)
}

export function cartItemCount(items: CartItem[]): number {
  return items.reduce((s, i) => s + i.quantity, 0)
}

// ── Reducer ───────────────────────────────────────────────────────────────────

function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case 'ADD_ITEM': {
      const existing = state.items.find(
        i => i.productId === action.item.productId && i.type === action.item.type
      )
      if (existing) {
        return {
          ...state,
          isOpen: true,
          items: state.items.map(i =>
            i.cartId === existing.cartId ? { ...i, quantity: i.quantity + (action.item.quantity ?? 1) } : i
          ),
        }
      }
      const cartId = `${action.item.productId}-${Date.now()}`
      return {
        ...state,
        isOpen: true,
        items: [...state.items, { ...action.item, cartId }],
      }
    }
    case 'REMOVE_ITEM':
      return { ...state, items: state.items.filter(i => i.cartId !== action.cartId) }
    case 'UPDATE_QTY':
      if (action.qty <= 0) return { ...state, items: state.items.filter(i => i.cartId !== action.cartId) }
      return { ...state, items: state.items.map(i => i.cartId === action.cartId ? { ...i, quantity: action.qty } : i) }
    case 'CLEAR':
      return { ...state, items: [], couponCode: null, discountPct: 0 }
    case 'OPEN':
      return { ...state, isOpen: true }
    case 'CLOSE':
      return { ...state, isOpen: false }
    case 'TOGGLE':
      return { ...state, isOpen: !state.isOpen }
    case 'HYDRATE':
      return { ...state, items: action.items }
    case 'APPLY_COUPON':
      return { ...state, couponCode: action.code, discountPct: action.discountPct }
    case 'REMOVE_COUPON':
      return { ...state, couponCode: null, discountPct: 0 }
    default:
      return state
  }
}

// ── Persistence helpers ───────────────────────────────────────────────────────

function storageKey(userId?: string | number | null): string {
  return userId ? `cor_cart_u${userId}` : 'cor_cart_guest'
}

function loadCart(userId?: string | number | null): CartItem[] {
  try {
    const raw = localStorage.getItem(storageKey(userId))
    if (!raw) {
      // cookie fallback for guests
      if (!userId) {
        const match = document.cookie.match(/(?:^|;\s*)cor_cart=([^;]*)/)
        if (match) return JSON.parse(decodeURIComponent(match[1]))
      }
      return []
    }
    return JSON.parse(raw)
  } catch { return [] }
}

function saveCart(items: CartItem[], userId?: string | number | null) {
  try {
    const json = JSON.stringify(items)
    localStorage.setItem(storageKey(userId), json)
    // also write a 7-day cookie for guests as fallback
    if (!userId) {
      const exp = new Date(Date.now() + 7 * 864e5).toUTCString()
      document.cookie = `cor_cart=${encodeURIComponent(json)}; expires=${exp}; path=/; SameSite=Lax`
    }
  } catch { /* quota exceeded */ }
}

// ── Context & Provider ────────────────────────────────────────────────────────

interface CartContextValue {
  state: CartState
  addItem: (item: Omit<CartItem, 'cartId'>) => void
  removeItem: (cartId: string) => void
  updateQty: (cartId: string, qty: number) => void
  clear: () => void
  openCart: () => void
  closeCart: () => void
  toggleCart: () => void
  applyCoupon: (code: string) => void
  removeCoupon: () => void
  subtotal: number
  total: number
  itemCount: number
}

const CartContext = createContext<CartContextValue | null>(null)

const INITIAL: CartState = { items: [], isOpen: false, couponCode: null, discountPct: 0 }

export function CartProvider({
  children,
  userId,
}: {
  children: React.ReactNode
  userId?: string | number | null
}) {
  const [state, dispatch] = useReducer(cartReducer, INITIAL)

  // Hydrate from storage on mount / user change
  useEffect(() => {
    const items = loadCart(userId)
    dispatch({ type: 'HYDRATE', items })
  }, [userId])

  // Persist on every items change
  useEffect(() => {
    saveCart(state.items, userId)
  }, [state.items, userId])

  const addItem = useCallback((item: Omit<CartItem, 'cartId'>) => dispatch({ type: 'ADD_ITEM', item }), [])
  const removeItem = useCallback((cartId: string) => dispatch({ type: 'REMOVE_ITEM', cartId }), [])
  const updateQty = useCallback((cartId: string, qty: number) => dispatch({ type: 'UPDATE_QTY', cartId, qty }), [])
  const clear = useCallback(() => dispatch({ type: 'CLEAR' }), [])
  const openCart = useCallback(() => dispatch({ type: 'OPEN' }), [])
  const closeCart = useCallback(() => dispatch({ type: 'CLOSE' }), [])
  const toggleCart = useCallback(() => dispatch({ type: 'TOGGLE' }), [])

  const applyCoupon = useCallback((code: string) => {
    const COUPONS: Record<string, number> = { ROYAL10: 10, WELCOME20: 20, GURUKUL15: 15, YTT25: 25 }
    const pct = COUPONS[code.toUpperCase()]
    if (pct) dispatch({ type: 'APPLY_COUPON', code, discountPct: pct })
  }, [])

  const removeCoupon = useCallback(() => dispatch({ type: 'REMOVE_COUPON' }), [])

  const subtotal = cartSubtotal(state.items)
  const total = cartTotal(state.items, state.discountPct)
  const itemCount = cartItemCount(state.items)

  return (
    <CartContext.Provider value={{
      state, addItem, removeItem, updateQty, clear,
      openCart, closeCart, toggleCart, applyCoupon, removeCoupon,
      subtotal, total, itemCount,
    }}>
      {children}
    </CartContext.Provider>
  )
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext)
  if (!ctx) throw new Error('useCart must be inside <CartProvider>')
  return ctx
}
